import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { students as initialStudents } from '../data/students';
import { paramedicStudents } from '../data/paramedicStudents';
import { db } from '../firebase';
import { collection, getDocs, addDoc, doc, getDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import SkillSheetsSelector from './LabDaySkillSheets';
import SkillSheetPreview from '../components/SkillSheetPreview';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const NewLabDay = () => {
  const [instructors, setInstructors] = useState([]);
  const [students, setStudents] = useState([]);
  const [presentStudents, setPresentStudents] = useState([]);
  const [unassignedStudents, setUnassignedStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [numGroups, setNumGroups] = useState(2);
  const [assignments, setAssignments] = useState([]);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [labTitle, setLabTitle] = useState('');
  const [labDate, setLabDate] = useState('');
  const [labDuration, setLabDuration] = useState(''); // store as string
  const [selectedClass, setSelectedClass] = useState(null);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [selectedSkillSheets, setSelectedSkillSheets] = useState([]); // New state for selected skill sheets
  // Add state for scroll indicator
  const [showScrollText, setShowScrollText] = useState(false);
  const scrollRef = useRef(null);
  const [pdfData, setPdfData] = useState(null);
  const printRef = useRef();

  const navigate = useNavigate();
  const { labId } = useParams(); // Get labId from URL

  // Load available classes from localStorage
  useEffect(() => {
    const loadAvailableClasses = () => {
      // Start with hardcoded classes
      let classes = [
        { id: 1, name: 'EMT 101 BEX SUM 2025', students: initialStudents },
        { id: 2, name: 'Paramedic 201', students: paramedicStudents },
      ];

      // Load imported classes from localStorage
      const savedClasses = localStorage.getItem('importedClasses');
      if (savedClasses) {
        try {
          const importedClasses = JSON.parse(savedClasses);
          // Merge imported classes with hardcoded ones
          const existingNames = classes.map(c => c.name);
          const newClasses = importedClasses.filter(c => !existingNames.includes(c.name));
          classes = [...classes, ...newClasses];
        } catch (error) {
          console.error('Error loading imported classes:', error);
        }
      }

      setAvailableClasses(classes);
    };

    loadAvailableClasses();
  }, []);

  // Set default selected class for new labs
  useEffect(() => {
    if (!labId && !selectedClass && availableClasses.length > 0) {
      setSelectedClass(availableClasses[0]);
    }
  }, [labId, selectedClass, availableClasses]);

  // Update students when selected class changes
  useEffect(() => {
    if (selectedClass && selectedClass.students) {
      setStudents(selectedClass.students);
      const allStudentIds = selectedClass.students.map(s => s.id);
      setPresentStudents(allStudentIds);
      setUnassignedStudents(selectedClass.students);
      // Clear existing groups when class changes
      setGroups([]);
      setAssignments([]);
    }
  }, [selectedClass]);

  // Add state for all skill sheets (for name lookup):
  const [allSkillSheets, setAllSkillSheets] = useState([]);

  useEffect(() => {
    const fetchInstructors = async () => {
        try {
            // Fetch instructors from the database
            const usersRef = collection(db, 'users');
            const usersSnap = await getDocs(usersRef);
            const instructorsList = usersSnap.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(user => user.role === 'Instructor')
                .map(instructor => ({
                    email: instructor.email,
                    name: instructor.displayName || instructor.email, // Use displayName if available, otherwise email
                    id: instructor.id
                }));
            
            setInstructors(instructorsList);
        } catch (error) {
            console.error('Error fetching instructors:', error);
            // Fallback to empty array if there's an error
            setInstructors([]);
        }
    };

    const fetchLabData = async () => {
        if (!labId) {
            // If no labId, it's a new lab, so set up initial student lists
            const defaultClass = availableClasses.find(c => c.name === 'EMT 101 BEX SUM 2025') || availableClasses[0];
            if (defaultClass) {
              setStudents(defaultClass.students);
              setSelectedClass(defaultClass);
              const allStudentIds = defaultClass.students.map(s => s.id);
              setPresentStudents(allStudentIds);
              setUnassignedStudents(defaultClass.students);
            }
            return;
        }

        const labDocRef = doc(db, 'labs', labId);
        const labDocSnap = await getDoc(labDocRef);

        if (labDocSnap.exists()) {
            const labData = labDocSnap.data();
            // When editing, we still need the full student list for the checkboxes
            setStudents(initialStudents);

            // Convert groups map back to array of arrays for the state
            let groupsArray = [];
            if (labData.groups && typeof labData.groups === 'object') {
                const allStudentsForClass = availableClasses.find(c => c.name === labData.className)?.students || initialStudents;
                groupsArray = Object.keys(labData.groups)
                    .sort() // Sort keys to maintain order, e.g., group_0, group_1, ...
                    .map(key => {
                        // labData.groups[key] might be an array of objects that are just {id: "...", name: "..."
                        // We need to map them back to the full student objects from our initial data
                        const groupStudentData = labData.groups[key] || [];
                        return groupStudentData.map(studentInGroup => {
                            return allStudentsForClass.find(fullStudent => fullStudent.id === studentInGroup.id) || studentInGroup;
                        });
                    });
                setGroups(groupsArray);
            } else {
                setGroups([]);
            }

            setAssignments(labData.assignments || []);
            setPresentStudents(labData.presentStudents || []);
            // Set lab info fields when editing
            setLabTitle(labData.labTitle || '');
            setLabDate(labData.labDate || '');
            setLabDuration(labData.labDuration || '');
            setSelectedClass(labData.className || null);
            setSelectedSkillSheets(labData.selectedSkillSheets || []);
            // Convert groupSkillSheets object back to array for state
            if (labData.groupSkillSheets && typeof labData.groupSkillSheets === 'object') {
              const groupSkillSheetsArray = Object.keys(labData.groupSkillSheets)
                .sort()
                .map(key => labData.groupSkillSheets[key]);
              setGroupSkillSheets(groupSkillSheetsArray);
            } else {
              setGroupSkillSheets(Array(groupsArray.length).fill([]));
            }
        } else {
            console.error("No such lab!");
            alert("Lab not found.");
            navigate('/lab-assignments');
        }
    };

    const fetchSkillSheets = async () => {
      const snap = await getDocs(collection(db, 'skillSheetTemplates'));
      setAllSkillSheets(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    fetchInstructors();
    fetchLabData();
    fetchSkillSheets();
  }, [labId, navigate, availableClasses]);

  // When class changes, update students and presentStudents
  useEffect(() => {
    if (!labId && selectedClass && Array.isArray(selectedClass.students)) {
      // New lab: update students and presentStudents on class change
      setStudents(selectedClass.students);
      const allStudentIds = selectedClass.students.map(s => s.id);
      setPresentStudents(allStudentIds);
      setUnassignedStudents(selectedClass.students);
      setGroups([]);
      setAssignments([]);
    }
    // For editing, student list is set in fetchLabData and should not be overwritten
  }, [selectedClass, labId]);

  // Add an effect to update numGroups based on instructor assignments
  useEffect(() => {
    const assignedInstructorCount = assignments.filter(email => email && email !== '').length;
    if (assignedInstructorCount > 0) {
      setNumGroups(assignedInstructorCount);
    }
  }, [assignments]);

  useEffect(() => {
    if (pdfData && printRef.current) {
      generatePdf(printRef.current, pdfData.labTitle || 'lab-day').then(() => {
        setPdfData(null); // Clear after generation
      });
    }
  }, [pdfData]);

  const generatePdf = (element, name) => {
    return new Promise((resolve, reject) => {
      if (!element) {
        reject(new Error("Element to print is not available."));
        return;
      }
      html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: true,
        backgroundColor: '#ffffff',
      }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const canvasAspectRatio = canvas.width / canvas.height;
        let imgWidth = pdfWidth;
        let imgHeight = imgWidth / canvasAspectRatio;
        if (imgHeight > pdfHeight) {
          imgHeight = pdfHeight;
          imgWidth = imgHeight * canvasAspectRatio;
        }
        const x = (pdfWidth - imgWidth) / 2;
        const y = (pdfHeight - imgHeight) / 2;
        pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
        pdf.save(`${name}.pdf`);
        resolve();
      }).catch(err => {
        console.error("Error generating PDF:", err);
        reject(err);
      });
    });
  };

  const saveLab = async () => {
    if (groups.length === 0) {
      alert("Please generate groups before saving.");
      return;
    }

    // Convert the groups array of arrays into a map to avoid nested arrays in Firestore
    const groupsAsMap = {};
    groups.forEach((group, index) => {
        // Ensure we don't save empty groups if a student was removed, etc.
        if (group.length > 0) {
            groupsAsMap[`group_${index}`] = group;
        }
    });

    // Convert groupSkillSheets array to object to avoid nested arrays in Firestore
    const groupSkillSheetsAsMap = {};
    groupSkillSheets.forEach((sheets, index) => {
      // Only allow arrays of plain objects with id and count (no nested arrays)
      if (Array.isArray(sheets)) {
        groupSkillSheetsAsMap[`group_${index}`] = sheets.map(s => ({ id: s.id, count: s.count }));
      } else {
        groupSkillSheetsAsMap[`group_${index}`] = [];
      }
    });

    const labData = {
      groups: groupsAsMap,
      assignments: assignments,
      presentStudents: presentStudents,
      className: selectedClass && selectedClass.name ? selectedClass.name : selectedClass || '',
      labTitle: labTitle,
      labDate: labDate,
      labDuration: labDuration,
      selectedSkillSheets: selectedSkillSheets, // Save selected skill sheets
      groupSkillSheets: groupSkillSheetsAsMap, // Save group skill sheets as object
    };

    try {
        if (labId) {
            // Update existing lab
            const labDocRef = doc(db, 'labs', labId);
            await updateDoc(labDocRef, labData);
            alert('Lab updated successfully!');
        } else {
            // Create new lab
            await addDoc(collection(db, "labs"), { ...labData, createdAt: serverTimestamp() });
            alert(`Lab saved successfully!`);
        }
        navigate('/lab-assignments');
    } catch (error) {
      console.error("Error saving document: ", error);
      alert("Failed to save lab: " + error.message);
    }
  };

  // Custom delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, onConfirm: null, message: '' });

  // Replace all window.confirm usages with custom modal
  const handleDelete = async () => {
    if (!labId) return;
    setDeleteModal({
      open: true,
      message: 'Are you sure you want to permanently delete this lab?',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, "labs", labId));
          alert("Lab deleted successfully.");
          navigate('/lab-assignments');
        } catch (error) {
          console.error("Error deleting document: ", error);
          alert("Failed to delete lab: " + error.message);
        }
        setDeleteModal({ open: false, onConfirm: null, message: '' });
      }
    });
  };

  useEffect(() => {
    // This effect is now split. Initial data setup is handled in the main useEffect.
  }, []);

  const handleStudentPresence = (studentId) => {
    const isPresent = presentStudents.includes(studentId);
    setPresentStudents(currentPresent => 
      isPresent ? currentPresent.filter(id => id !== studentId) : [...currentPresent, studentId]
    );

    if (isPresent) { // Student is being marked absent
      setUnassignedStudents(current => current.filter(s => s.id !== studentId));
      setGroups(currentGroups => currentGroups.map(g => g.filter(s => s.id !== studentId)));
    } else { // Student is being marked present
      const student = students.find(s => s.id === studentId);
      if (student) {
        // Only add to unassigned if not already in any group or unassigned
        const inGroups = groups.some(g => g.some(s => s.id === studentId));
        const inUnassigned = unassignedStudents.some(s => s.id === studentId);
        if (!inGroups && !inUnassigned) {
          setUnassignedStudents(current => [...current, student]);
        }
      }
    }
  };

  // Utility: sync groupSkillSheets to match groups length, preserving data
  function syncGroupSkillSheetsToGroups(groupSkillSheets, groups) {
    // If groupSkillSheets is undefined, start fresh
    if (!Array.isArray(groupSkillSheets)) return Array(groups.length).fill([]);
    // If more groups, add empty arrays; if fewer, trim
    if (groupSkillSheets.length < groups.length) {
      return [
        ...groupSkillSheets,
        ...Array(groups.length - groupSkillSheets.length).fill([])
      ];
    } else if (groupSkillSheets.length > groups.length) {
      return groupSkillSheets.slice(0, groups.length);
    }
    return groupSkillSheets;
  }

  const splitGroups = () => {
    // Always use all present students for group generation
    const presentStudentObjects = students.filter(s => presentStudents.includes(s.id));
    const shuffled = [...presentStudentObjects].sort(() => Math.random() - 0.5);
    const groupArr = Array.from({ length: numGroups }, () => []);
    let groupIndex = 0;
    shuffled.forEach((student) => {
      groupArr[groupIndex].push(student);
      groupIndex = (groupIndex + 1) % numGroups;
    });
    setGroups(groupArr);
    setUnassignedStudents([]); // All present students are now assigned
    setAssignments(Array(numGroups).fill(''));
    setGroupSkillSheets(prev => syncGroupSkillSheetsToGroups(prev, groupArr));
  };

  const handleAssignmentChange = (index, value) => {
    const newAssignments = [...assignments];
    // Ensure the array is long enough.
    while (newAssignments.length <= index) {
        newAssignments.push('');
    }
    newAssignments[index] = value;
    setAssignments(newAssignments);
  };

  const removeStudentFromGroup = (studentToRemove, groupIndex) => {
    const newGroups = [...groups];
    newGroups[groupIndex] = newGroups[groupIndex].filter(student => student.id !== studentToRemove.id);
    setGroups(newGroups);
    setUnassignedStudents(prev => [...prev, studentToRemove]);
  };

  const rebalanceGroups = () => {
    const allStudentsInGroups = groups.flat();
    const shuffled = [...allStudentsInGroups].sort(() => Math.random() - 0.5);
    const groupArr = Array.from({ length: groups.length }, () => []);
    shuffled.forEach((student, idx) => {
      groupArr[idx % groupArr.length].push(student);
    });
    setGroups(groupArr);
    setGroupSkillSheets(prev => syncGroupSkillSheetsToGroups(prev, groupArr));
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceId = source.droppableId;
    const destId = destination.droppableId;

    if (sourceId === destId) { // Reordering within a list
      if (sourceId === 'unassigned') {
        const newUnassigned = Array.from(unassignedStudents);
        const [moved] = newUnassigned.splice(source.index, 1);
        newUnassigned.splice(destination.index, 0, moved);
        setUnassignedStudents(newUnassigned);
      } else {
        const groupIndex = parseInt(sourceId.split('-')[1]);
        const newGroups = [...groups];
        const group = Array.from(newGroups[groupIndex]);
        const [moved] = group.splice(source.index, 1);
        group.splice(destination.index, 0, moved);
        newGroups[groupIndex] = group;
        setGroups(newGroups);
      }
    } else { // Moving between lists
      let sourceList, destList;
      let moved;

      // Find source list and remove item
      if (sourceId === 'unassigned') {
        sourceList = Array.from(unassignedStudents);
        [moved] = sourceList.splice(source.index, 1);
        setUnassignedStudents(sourceList);
      } else {
        const groupIndex = parseInt(sourceId.split('-')[1]);
        const newGroups = [...groups];
        sourceList = Array.from(newGroups[groupIndex]);
        [moved] = sourceList.splice(source.index, 1);
        newGroups[groupIndex] = sourceList;
        setGroups(newGroups);
      }

      // Find destination list and add item
      if (destId === 'unassigned') {
        destList = Array.from(unassignedStudents);
        destList.splice(destination.index, 0, moved);
        setUnassignedStudents(destList);
      } else {
        const groupIndex = parseInt(destId.split('-')[1]);
        const newGroups = [...groups];
        destList = Array.from(newGroups[groupIndex] || []);
        destList.splice(destination.index, 0, moved);
        newGroups[groupIndex] = destList;
        setGroups(newGroups);
      }
    }
  };

  // Calculate per-group session duration (subtract 5 min break per rotation)
  const breakTime = 5;
  const durationNum = parseInt(labDuration, 10);
  const effectiveDuration = (!isNaN(durationNum) ? durationNum : 0) - (numGroups - 1) * breakTime;
  const sessionDuration = numGroups > 0 ? Math.floor((!isNaN(durationNum) ? effectiveDuration : 0) / numGroups) : 0;

  // Robust scroll indicator logic
  useEffect(() => {
    if (!showUnassigned) {
      setShowScrollText(false);
      return;
    }
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      // Always show if not at bottom and there are students
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      setShowScrollText(!atBottom && unassignedStudents.length > 0 && el.scrollHeight > el.clientHeight + 2);
    };
    // Initial check
    handleScroll();
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [showUnassigned, unassignedStudents.length]);

  useEffect(() => {
    // When opening, check if scroll is needed
    if (showUnassigned && scrollRef.current) {
      const el = scrollRef.current;
      setShowScrollText(el.scrollHeight > el.clientHeight + 2 && unassignedStudents.length > 0);
    }
  }, [showUnassigned, unassignedStudents.length]);

  // Add new state for group skill sheet popups and group skill sheet assignments:
  const [groupSkillSheetPopups, setGroupSkillSheetPopups] = useState(Array(groups.length).fill(false));
  const [groupSkillSheets, setGroupSkillSheets] = useState(Array(groups.length).fill([]));

  // When groups change, sync popup and skill sheet arrays
  useEffect(() => {
    setGroupSkillSheetPopups(Array(groups.length).fill(false));
    setGroupSkillSheets(prev => syncGroupSkillSheetsToGroups(prev, groups));
  }, [groups]);

  // Helper to get available count for a sheet
  const getAvailableCount = (id) => {
    const selected = selectedSkillSheets.find(s => s.id === id);
    const groupCounts = groupSkillSheets.map(arr => Array.isArray(arr) ? (arr.find(s => s.id === id)?.count || 0) : 0);
    const totalAssigned = groupCounts.reduce((a, b) => a + b, 0);
    return selected ? Math.max(selected.count - totalAssigned, 0) : 0;
  };

  const GroupSkillSheetPopup = ({ availableSkillSheets, groupSkillSheets, setGroupSkillSheets, onClose, anchorRect, allSkillSheets, getAvailableCount }) => {
    // Always use the latest groupSkillSheets prop, not closure
    const addSheet = (id) => {
      // Only add if available globally
      if (getAvailableCount(id) <= 0) return;
      let newArr;
      if (!Array.isArray(groupSkillSheets)) newArr = [{ id, count: 1 }];
      else {
        const found = groupSkillSheets.find(s => s.id === id);
        if (found) {
          newArr = groupSkillSheets.map(s => s.id === id ? { ...s, count: s.count + 1 } : s);
        } else {
          newArr = [...groupSkillSheets, { id, count: 1 }];
        }
      }
      setGroupSkillSheets(newArr);
    };
    // Calculate popup style if anchorRect is provided
    const popupStyle = anchorRect
      ? {
          position: 'fixed',
          left: anchorRect.left + anchorRect.width / 2,
          top: anchorRect.top - 12, // 12px above button
          transform: 'translate(-50%, -100%)',
          zIndex: 9999,
          minWidth: 220,
          maxWidth: 320,
        }
      : { position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', zIndex: 9999 };
    // Only show sheets with available count > 0
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={popupStyle} className="bg-white rounded-xl p-4 border border-primary shadow-xl">
          <button onClick={onClose} className="absolute top-2 right-2 text-xl font-bold" type="button">×</button>
          <h3 className="text-lg font-bold mb-2 text-primary">Add Skill Sheets</h3>
          <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
            {availableSkillSheets.filter(s => getAvailableCount(s.id) > 0).length === 0 ? (
              <div className="text-gray-400 italic">No skill sheets available</div>
            ) : (
              availableSkillSheets.filter(s => getAvailableCount(s.id) > 0).map(({ id }) => {
                const sheet = allSkillSheets.find(s => s.id === id);
                return (
                  <div key={id} className="flex items-center gap-2 bg-gray-50 hover:bg-blue-50 rounded px-2 py-1">
                    <button
                      className="bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-base font-bold hover:bg-secondary hover:text-primary focus:outline-none"
                      onClick={e => { e.stopPropagation(); addSheet(id); }}
                      type="button"
                      style={{ fontSize: '1rem', padding: 0 }}
                    >
                      +
                    </button>
                    <span className="font-semibold text-text">{sheet ? sheet.name : id}</span>
                  </div>
                );
              })
            )}
          </div>
          <button className="mt-4 bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold w-full" onClick={onClose} type="button">Done</button>
        </div>
      </div>
    );
  };

  // Auto-remove group assignments for sheets no longer in selectedSkillSheets
  useEffect(() => {
    setGroupSkillSheets(prev => prev.map(arr =>
      arr.filter(s => selectedSkillSheets.some(sel => sel.id === s.id))
    ));
  }, [selectedSkillSheets]);

  // Add preview popup state and rendering
  const [previewSheet, setPreviewSheet] = useState(null);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="min-h-screen bg-bg text-text px-10 py-5">
        <h1 className="text-2xl font-bold mb-10 text-primary shadow-lg shadow-gray-200 rounded-2xl p-3">
          Create New Lab Day
          {selectedClass && (
            <span className="block text-lg text-secondary font-semibold mt-1">
              {selectedClass.name}
              {labTitle && ` | ${labTitle}`}
              {labDate && ` | ${new Date(labDate).toLocaleDateString()}`}
              {labDuration && ` | ${labDuration} min`}
            </span>
          )}
        </h1>

        <div className="flex justify-end gap-4 mb-4">
            <button 
                onClick={saveLab} 
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded shadow-lg transition duration-200"
            >
                {labId ? 'Update Lab' : 'Save Lab'}
            </button>
            <button 
                onClick={() => setPdfData({ labTitle, labDate, labDuration, groups, assignments, groupSkillSheets, sessionDuration, breakTime })} 
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded shadow-lg transition duration-200 flex items-center gap-2"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                Export PDF
            </button>
            {labId && (
                <button 
                    onClick={handleDelete} 
                    className="bg-red-600 hover:bg-red-800 text-white font-bold py-2 px-4 rounded shadow-lg transition duration-200"
                >
                    Delete Lab
                </button>
            )}
        </div>

        {/* Lab Info, Student/Instructor, & Group Generation Row */}
        <div className="flex flex-col md:flex-row gap-6 mb-8 justify-center items-stretch">
          {/* Lab Info Section */}
          <div className="mb-0 md:mb-0 md:w-1/3 p-6 border-2 border-primary rounded-2xl bg-white/80 shadow-lg max-w-3xl flex flex-col justify-between h-full min-h-[340px]">
            <h2 className="text-xl font-bold mb-4 text-primary">Lab Information</h2>
            <div className="flex flex-col gap-4 flex-1">
              <div className="flex items-center gap-4 min-h-[48px]">
                <label htmlFor="labTitle" className="w-32 font-semibold flex-shrink-0 text-right">Lab Title:</label>
                <input id="labTitle" type="text" value={labTitle} onChange={e => setLabTitle(e.target.value)} className="border rounded px-2 py-2 w-60 h-12 text-base align-middle" />
              </div>
              <div className="flex items-center gap-4 min-h-[48px]">
                <label htmlFor="labDate" className="w-32 font-semibold flex-shrink-0 text-right">Lab Date:</label>
                <input id="labDate" type="date" value={labDate} onChange={e => setLabDate(e.target.value)} className="border rounded px-2 py-2 w-60 h-12 text-base align-middle" />
              </div>
              <div className="flex items-center gap-4 min-h-[48px]">
                <label htmlFor="labDuration" className="w-32 font-semibold flex-shrink-0 text-right">Lab Duration (min):</label>
                <input id="labDuration" type="number" min={5} value={labDuration} onChange={e => setLabDuration(e.target.value)} className="border rounded px-2 py-2 w-60 h-12 text-base align-middle" />
              </div>
              <div className="flex items-center gap-4 min-h-[48px]">
                <label htmlFor="selectedClass" className="w-32 font-semibold flex-shrink-0 text-right">Class:</label>
                <select
                  id="selectedClass"
                  value={selectedClass?.name || ''}
                  onChange={e => {
                    const selectedClassData = availableClasses.find(c => c.name === e.target.value);
                    setSelectedClass(selectedClassData);
                  }}
                  className="border rounded px-2 py-2 w-60 h-12 text-base align-middle"
                >
                  {availableClasses.map(cls => (
                    <option key={cls.id} value={cls.name}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Skill Sheets Module (to the right of Lab Info) */}
          <div className="mb-0 md:mb-0 md:w-1/3 flex flex-col">
            <SkillSheetsSelector selectedSkillSheets={selectedSkillSheets} setSelectedSkillSheets={setSelectedSkillSheets} />
          </div>

          {/* Student & Instructor List Container (now in the middle) */}
          <div className="w-full md:w-1/3 p-6 border-2 border-primary rounded-2xl bg-white/80 shadow-lg flex flex-col items-stretch min-h-[340px] relative">
            <h2 className="text-xl font-bold mb-4 text-primary">Attendance</h2>
            <button
              onClick={() => setShowStudentModal(true)}
              className="btn-raised mb-4 bg-primary text-white px-4 py-2 rounded hover:bg-secondary hover:text-primary font-semibold w-full"
              type="button"
            >
              Show Student List
            </button>
            <button
              onClick={() => setShowInstructorModal(true)}
              className="btn-raised mb-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-800 font-semibold w-full"
              type="button"
            >
              Show Lab Instructor List
            </button>
            {/* Expandable Toggle for Unassigned Students */}
            <button
              onClick={() => setShowUnassigned(v => !v)}
              className="btn-raised mb-2 bg-gray-200 hover:bg-gray-300 text-text font-semibold px-4 py-2 rounded flex items-center justify-between w-full"
              aria-expanded={showUnassigned}
              aria-controls="unassigned-students-list"
              type="button"
            >
              <span>{showUnassigned ? 'Hide' : 'Show'} Unassigned Students ({unassignedStudents.length})</span>
              <svg className={`ml-2 w-4 h-4 transition-transform duration-200 ${showUnassigned ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            </button>
            {showUnassigned && (
              <Droppable droppableId="unassigned">
                {(provided, snapshot) => (
                  <div
                    id="unassigned-students-list"
                    {...provided.droppableProps}
                    ref={el => {
                      provided.innerRef(el);
                      scrollRef.current = el;
                    }}
                    className={`p-4 border border-dashed rounded-xl relative overflow-y-scroll overflow-x-scroll scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 ${snapshot.isDraggingOver ? 'bg-blue-100' : 'bg-table'}`}
                    style={{ maxHeight: '300px', minHeight: '60px', scrollbarColor: '#9ca3af #f3f4f6', scrollbarWidth: 'thin' }}
                  >
                    {/* Top fade for scrollable indication */}
                    <div className="pointer-events-none absolute left-0 right-0 top-0 h-6 bg-gradient-to-b from-gray-200/80 to-transparent z-10" style={{display: unassignedStudents.length > 4 ? 'block' : 'none'}} />
                    {/* Bottom fade for scrollable indication */}
                    <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-6 bg-gradient-to-t from-gray-200/80 to-transparent z-10" style={{display: unassignedStudents.length > 4 ? 'block' : 'none'}} />
                    {/* Always show a fake scrollbar if not enough items to scroll */}
                    <div style={{position:'absolute',top:0,right:0,width:'8px',height:'100%',pointerEvents:'none',zIndex:20,opacity:unassignedStudents.length > 4 ? 1 : 0.4,background:'linear-gradient(to bottom, #e5e7eb 60%, transparent 100%)'}}></div>
                    {/* No scroll indicator here anymore */}
                    {unassignedStudents.length === 0 && <div className="text-gray-500 italic">No unassigned students</div>}
                    {unassignedStudents.map((student, index) => (
                      <Draggable key={student.id} draggableId={student.id} index={index}>
                        {(provided, snapshot) => (
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className={`p-2 mb-2 rounded min-w-[180px] ${snapshot.isDragging ? 'bg-blue-200' : 'bg-white'}`}> 
                            {student.firstName} {student.lastName}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
            {/* Scroll indicator absolutely positioned in card bottom right */}
            {showScrollText && (
              <div style={{position: 'absolute', bottom: 12, right: 12, zIndex: 100, pointerEvents: 'none'}}>
                <span className="text-xs text-gray-700 font-bold animate-fadeinout select-none px-3 py-2 rounded bg-white/90 shadow-lg border border-gray-200" style={{opacity: 0.95, transition: 'opacity 0.7s', minWidth: '56px', maxWidth: '100px', pointerEvents: 'none'}}>scroll ↓</span>
              </div>
            )}
          </div>

          {/* Group Generation Section */}
          <div className="md:w-1/3 p-6 border-2 border-primary rounded-2xl bg-white/80 shadow-lg flex flex-col justify-between h-full min-h-[340px]">
            <h2 className="text-xl font-bold mb-4 text-primary">Group Generation & Assignment</h2>
            <div className="mb-4 text-lg font-semibold text-primary">
              Each group session: {sessionDuration > 0 ? sessionDuration : 0} min
              {numGroups > 1 && (
                <span> &nbsp;|&nbsp; 5 min break between rotations</span>
              )}
            </div>
            <div className="mb-4 flex flex-col items-center gap-4 w-full">
              <div className="flex items-center gap-4 justify-center w-full">
                <label className="font-semibold mr-2">Number of Groups:</label>
                <input type="number" min={1} max={presentStudents.length} value={numGroups} onChange={e => setNumGroups(Number(e.target.value))} className="border rounded px-2 py-1 w-20"/>
              </div>
              <div className="flex gap-4 justify-center w-full">
                <button
                  className="btn-raised bg-primary text-white px-4 py-2 rounded hover:bg-secondary hover:text-primary font-semibold"
                  onClick={splitGroups}
                  disabled={presentStudents.length === 0 || numGroups < 1 || numGroups > presentStudents.length}
                  type="button"
                >
                  Generate Groups
                </button>
                {groups.length > 0 && (
                  <button
                    className="btn-raised bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-700 font-semibold"
                    onClick={rebalanceGroups}
                    type="button"
                  >
                    Rebalance Groups
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6 mt-6">
          {/* Student List Modal */}
          {showStudentModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={e => { if (e.target === e.currentTarget) setShowStudentModal(false); }}>
              <div className="bg-white text-black rounded-xl p-6 w-full max-w-lg relative">
                <button
                  className="btn-raised absolute top-2 right-2 text-xl font-bold"
                  onClick={() => setShowStudentModal(false)}
                  type="button"
                >×</button>
                <h2 className="text-xl font-bold mb-4">Master Student List (Present: {presentStudents.length}/{students.length})</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-96 overflow-y-auto">
                  {students.map(student => (
                    <div key={student.id} className="flex items-center">
                      <input type="checkbox" id={`student-${student.id}`} checked={presentStudents.includes(student.id)} onChange={() => handleStudentPresence(student.id)} className="mr-2"/>
                      <label htmlFor={`student-${student.id}`} className={`${presentStudents.includes(student.id) ? '' : 'line-through text-gray-500'}`}>{student.firstName} {student.lastName}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Instructor List Modal */}
          {showInstructorModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={e => { if (e.target === e.currentTarget) setShowInstructorModal(false); }}>
              <div className="bg-white text-black rounded-xl p-6 w-full max-w-lg relative">
                <button
                  className="btn-raised absolute top-2 right-2 text-xl font-bold"
                  onClick={() => setShowInstructorModal(false)}
                  type="button"
                >×</button>
                <h2 className="text-xl font-bold mb-4">Lab Instructor List</h2>
                <div className="mb-4 grid grid-cols-1 gap-2">
                  {instructors.map(inst => (
                    <label key={inst.email} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={inst.present || false}
                        onChange={() => {
                          setInstructors(prev => prev.map(i =>
                            i.email === inst.email ? { ...i, present: !i.present } : i
                          ));
                        }}
                      />
                      {inst.name}
                    </label>
                  ))}
                </div>
                <button
                  className="btn-raised mb-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-800 font-semibold w-full"
                  onClick={() => {
                    const presentInstructors = instructors.filter(i => i.present);
                    if (presentInstructors.length === 0) {
                      alert('Please select at least one instructor.');
                      return;
                    }
                    // If no groups, auto-generate groups based on present instructors
                    if (groups.length === 0) {
                      const presentStudentObjects = students.filter(s => presentStudents.includes(s.id));
                      const numAutoGroups = presentInstructors.length;
                      const shuffled = [...presentStudentObjects].sort(() => Math.random() - 0.5);
                      const groupArr = Array.from({ length: numAutoGroups }, () => []);
                      let groupIndex = 0;
                      shuffled.forEach((student) => {
                        groupArr[groupIndex].push(student);
                        groupIndex = (groupIndex + 1) % numAutoGroups;
                      });
                      setGroups(groupArr);
                      setUnassignedStudents([]);
                      setAssignments(Array(numAutoGroups).fill(''));
                      setTimeout(() => {
                        setAssignments(groupArr.map((_, idx) => presentInstructors[idx]?.email || ''));
                      }, 0);
                    } else {
                      if (presentInstructors.length !== groups.length) {
                        alert('The number of present instructors must match the number of groups.');
                        return;
                      }
                      setAssignments(groups.map((_, idx) => presentInstructors[idx]?.email || ''));
                    }
                    setShowInstructorModal(false);
                  }}
                  disabled={presentStudents.length === 0}
                  type="button"
                >
                  Assign to Groups
                </button>
              </div>
            </div>
          )}

          <div className="w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {groups.map((group, idx) => (
                <Droppable key={idx} droppableId={`group-${idx}`}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className={`p-4 rounded-2xl shadow-lg transition-all duration-300 ${snapshot.isDraggingOver ? 'bg-green-100' : 'bg-white/90'} border-2 border-primary flex flex-col`}>
                      <div className="flex justify-between items-center mb-2 pb-2 border-b-2 border-primary/50">
                        <h2 className="text-xl font-bold text-primary">Group {idx + 1}</h2>
                        <div className="text-sm text-gray-600 font-semibold">
                            <span>{sessionDuration > 0 ? `${sessionDuration} min` : 'N/A'}</span>
                            {idx < groups.length - 1 && <span className="text-gray-500"> + {breakTime} min break</span>}
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <label htmlFor={`instructor-${idx}`} className="mr-2 font-semibold text-sm">Instructor:</label>
                        <div className="relative inline-block text-left w-full">
                          <select
                            value={assignments[idx] || ''}
                            onChange={e => handleAssignmentChange(idx, e.target.value)}
                            className="p-2 border rounded-md bg-card text-card-foreground shadow-sm appearance-none pr-8 w-full"
                          >
                            <option value="">Assign Instructor</option>
                            {instructors.map(inst => <option key={inst.email} value={inst.email}>{inst.name}</option>)}
                          </select>
                        </div>
                      </div>
                      {/* Skill Sheets for Group */}
                      <div className="mb-2 flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-primary">Skill Sheets</span>
                          <div className="relative">
                            <button
                              className="ml-2 px-2 py-1 bg-primary text-white rounded hover:bg-secondary hover:text-primary text-xs font-semibold"
                              onClick={e => {
                                const rect = e.target.getBoundingClientRect();
                                setGroupSkillSheetPopups(p => p.map((v, i) => i === idx ? { open: true, anchorRect: rect } : v));
                              }}
                              type="button"
                            >
                              + Assign to Group
                            </button>
                            {groupSkillSheetPopups[idx] && (
                              <GroupSkillSheetPopup
                                availableSkillSheets={selectedSkillSheets}
                                groupSkillSheets={groupSkillSheets[idx]}
                                setGroupSkillSheets={sheets =>
                                  setGroupSkillSheets(gs => {
                                    const newArr = gs.map((arr, i) => (i === idx ? sheets : arr));
                                    return newArr;
                                  })
                                }
                                onClose={() => setGroupSkillSheetPopups(p => p.map((v, i) => i === idx ? false : v))}
                                anchorRect={groupSkillSheetPopups[idx]?.anchorRect}
                                allSkillSheets={allSkillSheets}
                                getAvailableCount={getAvailableCount}
                                key={`popup-${idx}-${JSON.stringify(groupSkillSheets[idx])}`}
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Array.isArray(groupSkillSheets[idx]) && groupSkillSheets[idx].length === 0 && (
                            <span className="text-xs text-gray-400 italic">No skill sheets added</span>
                          )}
                          {(Array.isArray(groupSkillSheets[idx]) ? groupSkillSheets[idx] : []).map(({ id, count }) => {
                            const sheet = allSkillSheets.find(s => s.id === id);
                            if (!sheet || count === 0) return null;
                            return (
                              <button
                                key={id}
                                className="flex items-center justify-center gap-2 text-xs bg-primary text-white rounded-full px-3 py-1 border border-primary shadow hover:bg-secondary hover:text-primary transition-colors w-full text-center relative group"
                                style={{ fontWeight: 600, minHeight: '1.75rem' }}
                                type="button"
                                title={sheet.name}
                                onClick={e => {
                                  e.stopPropagation();
                                  setPreviewSheet(sheet);
                                }}
                              >
                                <span className="flex-1 text-center">{sheet.name}</span>
                                {count > 1 && <span className="ml-1 bg-white text-primary rounded-full px-2 py-0.5 text-xs font-bold">{count}</span>}
                                <span
                                  className="ml-1 text-white hover:text-red-300 text-base font-bold cursor-pointer"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setGroupSkillSheets(gs => gs.map((arr, i) => {
                                      if (i !== idx) return arr;
                                      const found = arr.find(s => s.id === id);
                                      if (!found) return arr;
                                      if (found.count > 1) {
                                        return arr.map(s => s.id === id ? { ...s, count: s.count - 1 } : s);
                                      } else {
                                        return arr.filter(s => s.id !== id);
                                      }
                                    }));
                                  }}
                                  title="Remove one"
                                  style={{ lineHeight: 1 }}
                                >×</span>
                                {/* Preview icon, only show on hover */}
                                <span
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity duration-200"
                                  title="Preview"
                                  style={{ fontSize: '1.1em', pointerEvents: 'none' }}
                                >
                                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12c-4.97 0-8.13-4.11-8.94-5C3.87 10.11 7.03 6 12 6s8.13 4.11 8.94 5c-.81.89-3.97 5-8.94 5zm0-8a3 3 0 100 6 3 3 0 000-6zm0 4a1 1 0 110-2 1 1 0 010 2z" clipRule="evenodd" /></svg>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {group.map((student, index) => (
                        <Draggable key={student.id} draggableId={student.id} index={index}>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className={`flex justify-between items-center p-2 mb-2 rounded ${snapshot.isDragging ? 'bg-green-200' : 'bg-white'}`}> 
                              <span>{student.lastName}, {student.firstName}</span>
                              <button onClick={() => removeStudentFromGroup(student, idx)} className="btn-raised text-red-500 hover:text-red-700 font-bold">X</button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              ))}
            </div>
          </div>
        </div>

        {previewSheet && (
          <SkillSheetPreview 
            template={previewSheet} 
            onClose={() => setPreviewSheet(null)} 
          />
        )}

        {pdfData && (
            <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                <LabDayPDFPreview 
                    ref={printRef} 
                    labData={pdfData} 
                    allSkillSheets={allSkillSheets} 
                    students={students} 
                    instructors={instructors} 
                />
            </div>
        )}

        {deleteModal.open && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-xs border-2 border-red-500 shadow-xl relative">
              <h2 className="text-xl font-bold text-red-600 mb-4">Confirm Delete</h2>
              <div className="mb-6 text-gray-700">{deleteModal.message}</div>
              <div className="flex gap-4 justify-end">
                <button className="btn-raised bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 font-semibold" onClick={() => setDeleteModal({ open: false, onConfirm: null, message: '' })} type="button">Cancel</button>
                <button className="btn-raised bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 font-semibold" onClick={() => { deleteModal.onConfirm && deleteModal.onConfirm(); }} type="button">Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DragDropContext>
  );
};

const LabDayPDFPreview = React.forwardRef(({ labData, allSkillSheets, students, instructors }, ref) => {
    if (!labData) return null;
  
    const { labTitle, labDate, labDuration, groups, assignments, groupSkillSheets, sessionDuration, breakTime } = labData;
  
    return (
      <div ref={ref} className="p-8 bg-white font-sans text-gray-800" style={{ width: '210mm', minHeight: '297mm' }}>
        <h1 className="text-3xl font-bold mb-2 text-center text-blue-700">{labTitle || '(Untitled Lab)'}</h1>
        <div className="flex justify-around mb-6 text-base border-b pb-2">
          <span className="font-semibold">Date: <span className="font-normal">{labDate ? new Date(labDate + 'T00:00:00').toLocaleDateString() : 'N/A'}</span></span>
          <span className="font-semibold">Duration: <span className="font-normal">{labDuration || 'N/A'} min</span></span>
          <span className="font-semibold">Rotation Length: <span className="font-normal">{sessionDuration || 'N/A'} min</span></span>
        </div>
  
        {groups.map((group, groupIndex) => {
            const instructor = instructors.find(i => i.email === assignments[groupIndex]);
            return (
                <div key={groupIndex} className="mb-6 p-4 border border-gray-300 rounded-lg break-inside-avoid">
                    <div className="flex justify-between items-center mb-3 border-b pb-2">
                        <h2 className="text-xl font-bold text-blue-600">
                            Group {groupIndex + 1}
                            {instructor && <span className="font-semibold text-gray-700 text-lg"> - {instructor.name}</span>}
                        </h2>
                        <div className="text-base font-semibold text-gray-700">
                            <span>Rotation: {sessionDuration > 0 ? `${sessionDuration} min` : 'N/A'}</span>
                            {groupIndex < groups.length - 1 && <span> + {breakTime} min break</span>}
                        </div>
                    </div>
            
                    <div className="grid grid-cols-2 gap-x-6">
                        <div>
                            <h3 className="font-semibold text-lg mb-2 underline">Students</h3>
                            {group.length > 0 ? (
                                <ul className="list-disc list-inside space-y-1">
                                    {group.map(student => {
                                        return <li key={student.id}>{student.lastName}, {student.firstName}</li>;
                                    })}
                                </ul>
                            ) : (
                                <p className="text-gray-500 italic">No students in this group.</p>
                            )}
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg mb-2 underline">Assigned Skill Sheets</h3>
                            {(groupSkillSheets[groupIndex] && groupSkillSheets[groupIndex].length > 0) ? (
                                <ul className="list-disc list-inside space-y-1">
                                    {groupSkillSheets[groupIndex].map(sheet => {
                                        const sheetDetails = allSkillSheets.find(s => s.id === sheet.id);
                                        return (
                                        <li key={sheet.id}>
                                            {sheetDetails ? sheetDetails.name : 'Unknown Sheet'} (x{sheet.count})
                                        </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <p className="text-gray-500 italic">No skill sheets assigned.</p>
                            )}
                        </div>
                    </div>
                </div>
            );
        })}
      </div>
    );
});

export default NewLabDay;

/* GLOBAL BUTTON STYLES (add to index.css or tailwind config):
.btn-raised {
  @apply shadow-lg active:shadow-inner active:translate-y-1 transition-transform transition-colors duration-200;
}

// Example usage: <button className="btn-raised ...other classes...">...</button>
*/

/*
Add this to your index.css if not already present:
.animate-fadeinout {
  animation: fadeinout 2.5s linear infinite;
}
@keyframes fadeinout {
  0% { opacity: 0.2; }
  20% { opacity: 0.95; }
  80% { opacity: 0.95; }
  100% { opacity: 0.2; }
}
*/
